import { useEffect, useMemo, useState } from 'react';
import { api } from '../../api/client.js';

/** Valores fictícios só para pré-visualização no painel (não são enviados). */
const EMAIL_PREVIEW_SAMPLE_VARS = {
  tipoCadastro: 'Nova loja',
  nome: 'Maria Silva',
  email: 'maria.silva@exemplo.pt',
  plano: 'Pro',
  nomeLoja: 'Pastelaria do Bairro',
  slug: 'pastelaria-bairro',
  userId: '42',
  donoUserId: '42',
  emailDono: 'dono@exemplo.pt',
  tenantId: '7',
  data: '2026-05-09 14:30:00',
  numeroPedido: 'PED-00000A1B',
  pedidoId: '4127',
  nomeCliente: 'Maria Silva',
  telefoneCliente: '+351 912 345 678',
  emailCliente: 'maria.silva@exemplo.pt',
  itensResumo: '• 2x Pastel de nata — 3,00 €\n• 1x Bolo de arroz — 2,50 €',
  total: '5,50 €',
  formaPagamento: 'MB Way',
  observacoes: 'Entregar ao porteiro',
  telefoneLoja: '213456789',
  telefoneLojaFormatado: '(21) 3456-7890',
  linkLoja: 'https://exemplo.omini.app/pastelaria-bairro',
  totalLogs: '3120',
  totalErp: '2800',
  totalAuditNoise: '320',
  linkLimpar: 'https://exemplo.omini.app/api/admin/logs/cleanup?token=…',
  subtotal: '5,50 €',
  descontoPercent: '0',
  cupomCodigo: '—',
  valorDesconto: '0,00 €',
  blocoCupom: '',
  taxaEntrega: '2,50 €',
  zonaEntrega: 'Centro',
  blocoFrete: '🚚 *Taxa de entrega:* 2,50 € (Centro)\n\n',
};

function fillEmailPreviewPlaceholders(str) {
  if (typeof str !== 'string' || !str) return '';
  return str.replace(/\{([^}]+)\}/g, (_, raw) => {
    const key = raw.trim();
    if (Object.prototype.hasOwnProperty.call(EMAIL_PREVIEW_SAMPLE_VARS, key)) {
      return EMAIL_PREVIEW_SAMPLE_VARS[key];
    }
    return `{${key}}`;
  });
}

/** Substitui variáveis com mapa explícito (ex.: link real de limpeza de logs). */
function fillTemplatePlaceholders(str, vars) {
  if (typeof str !== 'string' || !str) return '';
  return str.replace(/\{([^}]+)\}/g, (_, raw) => {
    const key = raw.trim();
    if (vars && Object.prototype.hasOwnProperty.call(vars, key)) {
      return String(vars[key]);
    }
    return `{${key}}`;
  });
}

function wrapEmailPreviewDocument(bodyHtml) {
  const safe = bodyHtml ?? '';
  return `<!DOCTYPE html><html lang="pt"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><style>body{margin:0;padding:16px;font-family:system-ui,-apple-system,sans-serif;font-size:15px;line-height:1.5;background:#e8e8ea;color:#111} img{max-width:100%;height:auto}</style></head><body>${safe}</body></html>`;
}

function Toggle({ on, onChange, label }) {
  return (
    <label className="flex cursor-pointer items-center gap-2 text-sm font-semibold text-ink dark:text-mist">
      <button
        type="button"
        role="switch"
        aria-checked={on}
        onClick={() => onChange(!on)}
        className={`relative h-7 w-12 shrink-0 rounded-full transition-colors ${
          on ? 'bg-accent' : 'bg-ink/20 dark:bg-white/20'
        }`}
      >
        <span
          className={`absolute top-0.5 left-0.5 h-6 w-6 rounded-full bg-white shadow transition-transform ${
            on ? 'translate-x-5' : 'translate-x-0'
          }`}
        />
      </button>
      {label}
    </label>
  );
}

/**
 * Chip compacto de estado — leitura rápida (verde / âmbar / zinco).
 * @param {{ label: string, value: string, tone?: 'ok' | 'warn' | 'off' | 'info', detail?: string }} props
 */
function TemplateKpiChip({ label, value, tone = 'info', detail }) {
  const toneCls =
    tone === 'ok'
      ? 'border-emerald-500/45 bg-emerald-500/[0.12] text-emerald-950 dark:border-emerald-400/40 dark:bg-emerald-400/15 dark:text-emerald-100'
      : tone === 'warn'
        ? 'border-amber-500/45 bg-amber-500/[0.12] text-amber-950 dark:border-amber-400/40 dark:bg-amber-400/15 dark:text-amber-50'
        : tone === 'off'
          ? 'border-zinc-400/35 bg-zinc-500/[0.08] text-zinc-700 dark:border-white/15 dark:bg-white/[0.04] dark:text-frost-muted'
          : 'border-sky-500/40 bg-sky-500/[0.10] text-sky-950 dark:border-sky-400/35 dark:bg-sky-400/12 dark:text-sky-100';
  const dotCls =
    tone === 'ok'
      ? 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.65)]'
      : tone === 'warn'
        ? 'bg-amber-500 shadow-[0_0_8px_rgba(245,158,11,0.55)]'
        : tone === 'off'
          ? 'bg-zinc-400'
          : 'bg-sky-500 shadow-[0_0_8px_rgba(14,165,233,0.5)]';

  return (
    <div
      className={`flex min-w-[7.5rem] flex-1 items-center gap-2 rounded-xl border px-2.5 py-1.5 ${toneCls}`}
      title={detail || undefined}
    >
      <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${dotCls}`} aria-hidden />
      <div className="min-w-0 leading-tight">
        <p className="text-[9px] font-bold uppercase tracking-[0.14em] opacity-70">{label}</p>
        <p className="truncate text-[12px] font-semibold tabular-nums">{value}</p>
      </div>
    </div>
  );
}

/** @param {{ enabled: boolean }} props */
function TemplateActivePill({ enabled }) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide ${
        enabled
          ? 'bg-emerald-500/20 text-emerald-800 dark:bg-emerald-400/20 dark:text-emerald-100'
          : 'bg-zinc-500/15 text-zinc-600 dark:bg-white/10 dark:text-frost-muted'
      }`}
    >
      <span
        className={`h-1.5 w-1.5 rounded-full ${enabled ? 'bg-emerald-500' : 'bg-zinc-400'}`}
        aria-hidden
      />
      {enabled ? 'Ativo' : 'Off'}
    </span>
  );
}

/** Pré-visualização estilo WhatsApp (coluna direita). */
function WhatsAppLivePreview({ text }) {
  const filled = fillEmailPreviewPlaceholders(text || '');
  const html = escapeHtml(filled)
    .replace(/\*(.+?)\*/g, '<strong>$1</strong>')
    .replace(/_(.+?)_/g, '<em>$1</em>')
    .replace(/~(.+?)~/g, '<s>$1</s>')
    .replace(/\n/g, '<br/>');

  return (
    <div className="flex h-full min-h-[220px] flex-col overflow-hidden rounded-2xl border border-emerald-500/35 bg-[#0b1410] shadow-inner">
      <div className="flex items-center gap-2 border-b border-emerald-400/20 bg-emerald-950/80 px-3 py-2">
        <span className="h-2 w-2 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.8)]" />
        <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-emerald-100/90">
          Pré-visualização WhatsApp
        </p>
      </div>
      <div
        className="relative flex-1 overflow-auto p-4"
        style={{
          backgroundImage:
            'radial-gradient(circle at 20% 20%, rgba(16,185,129,0.12), transparent 40%), radial-gradient(circle at 80% 0%, rgba(45,212,191,0.08), transparent 35%), repeating-linear-gradient(0deg, transparent, transparent 11px, rgba(255,255,255,0.02) 11px, rgba(255,255,255,0.02) 12px)',
        }}
      >
        <div className="ml-auto max-w-[92%] rounded-2xl rounded-tr-sm bg-[#005c4b] px-3 py-2 text-[12px] leading-relaxed text-[#e7ffef] shadow-lg">
          {filled.trim() ? (
            <div dangerouslySetInnerHTML={{ __html: html }} />
          ) : (
            <span className="opacity-60">Escreva a mensagem à esquerda…</span>
          )}
        </div>
      </div>
    </div>
  );
}

/** Pré-visualização e-mail ao vivo (coluna direita). */
function EmailLivePreview({ subject, html }) {
  const subj = fillEmailPreviewPlaceholders(subject || '');
  const body = fillEmailPreviewPlaceholders(html || '');
  return (
    <div className="flex h-full min-h-[280px] flex-col overflow-hidden rounded-2xl border border-sky-500/35 bg-[#0c1218] shadow-inner">
      <div className="border-b border-sky-400/20 bg-sky-950/70 px-3 py-2">
        <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-sky-100/90">
          Pré-visualização e-mail
        </p>
        <p className="mt-0.5 truncate text-[11px] text-sky-100/70" title={subj}>
          {subj.trim() ? subj : '(sem assunto)'}
        </p>
      </div>
      <div className="min-h-0 flex-1 bg-[#e8e8ea] p-2">
        <iframe
          title="Pré-visualização ao vivo"
          className="h-[min(52vh,420px)] w-full rounded-lg border-0 bg-white"
          sandbox=""
          srcDoc={wrapEmailPreviewDocument(body)}
        />
      </div>
    </div>
  );
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Legenda de variáveis — contraste legível em claro e escuro. */
const TEMPLATE_LEGEND_CLS =
  'mt-2 rounded-xl border border-ink/10 bg-ink/[0.04] px-3 py-2 text-[11px] leading-relaxed text-ink/75 dark:border-white/15 dark:bg-white/[0.07] dark:text-white/85';

/** @param {{ users: object[], onError: (s: string) => void, onSuccess: (s: string) => void }} props */
export function AdminTemplatesSection({ users, onError, onSuccess }) {
  const [sub, setSub] = useState('whatsapp');
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState(null);
  const [waTokenInput, setWaTokenInput] = useState('');
  const [smtpPassInput, setSmtpPassInput] = useState('');
  const [showWaToken, setShowWaToken] = useState(false);
  const [showSmtpPass, setShowSmtpPass] = useState(false);
  const [savingWa, setSavingWa] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);
  const [broadcastSubject, setBroadcastSubject] = useState('');
  const [broadcastBody, setBroadcastBody] = useState('');
  const [broadcastMode, setBroadcastMode] = useState('user');
  const [broadcastUserId, setBroadcastUserId] = useState('');
  const [broadcastBusy, setBroadcastBusy] = useState(false);
  const [testEmailAddr, setTestEmailAddr] = useState('');
  const [testEmailBusy, setTestEmailBusy] = useState(false);
  const [logsCleanupAlertBusy, setLogsCleanupAlertBusy] = useState(false);
  const [emailPreview, setEmailPreview] = useState(null);

  useEffect(() => {
    if (!emailPreview) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setEmailPreview(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [emailPreview]);

  async function reload() {
    setLoading(true);
    onError('');
    try {
      const data = await api('/admin/notification-settings');
      setDraft(data);
    } catch (e) {
      onError(e.message || 'Erro ao carregar templates');
      setDraft(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    reload();
  }, []);

  const waTemplates = draft?.whatsapp?.templates ?? [];
  const emTemplates = draft?.email?.templates ?? [];
  const smtp = draft?.email?.smtp ?? {};

  const regTemplate = useMemo(
    () => emTemplates.find((t) => t.event_key === 'admin_new_registration'),
    [emTemplates]
  );
  const logsCleanupTemplate = useMemo(
    () => emTemplates.find((t) => t.event_key === 'admin_logs_cleanup_prompt'),
    [emTemplates]
  );
  const broadTemplate = useMemo(
    () => emTemplates.find((t) => t.event_key === 'admin_email_broadcast'),
    [emTemplates]
  );

  const builtInEmailEventKeys = useMemo(
    () => new Set(['admin_new_registration', 'admin_email_broadcast', 'admin_logs_cleanup_prompt']),
    []
  );
  const extraEmailTemplates = useMemo(
    () => emTemplates.filter((t) => !builtInEmailEventKeys.has(t.event_key)),
    [emTemplates, builtInEmailEventKeys]
  );

  const templateKpis = useMemo(() => {
    const waActive = waTemplates.filter((t) => t.enabled).length;
    const emActive = emTemplates.filter((t) => t.enabled).length;
    const alertRaw = String(smtp.alert_to_emails || '').trim();
    const alertCount = alertRaw
      ? alertRaw.split(/[,;]+/).map((s) => s.trim()).filter(Boolean).length
      : 0;
    const waTok = Boolean(draft?.whatsapp?.token_configured);
    const smtpOn = Boolean(smtp.enabled);
    const smtpPass = Boolean(smtp.smtp_password_configured);
    return {
      waActive,
      waTotal: waTemplates.length,
      emActive,
      emTotal: emTemplates.length,
      alertCount,
      waTok,
      smtpOn,
      smtpPass,
      waEndpoint: String(draft?.whatsapp?.endpoint_url || '').trim() !== '',
    };
  }, [waTemplates, emTemplates, smtp, draft]);

  function patchWaTemplate(eventKey, patch) {
    setDraft((d) => {
      if (!d?.whatsapp?.templates) return d;
      return {
        ...d,
        whatsapp: {
          ...d.whatsapp,
          templates: d.whatsapp.templates.map((t) =>
            t.event_key === eventKey ? { ...t, ...patch } : t
          ),
        },
      };
    });
  }

  function patchEmailTemplate(eventKey, patch) {
    setDraft((d) => {
      if (!d?.email?.templates) return d;
      return {
        ...d,
        email: {
          ...d.email,
          templates: d.email.templates.map((t) =>
            t.event_key === eventKey ? { ...t, ...patch } : t
          ),
        },
      };
    });
  }

  function patchSmtp(patch) {
    setDraft((d) => {
      if (!d?.email) return d;
      return {
        ...d,
        email: {
          ...d.email,
          smtp: { ...d.email.smtp, ...patch },
        },
      };
    });
  }

  async function saveWhatsapp() {
    if (!draft) return;
    setSavingWa(true);
    onError('');
    try {
      const body = {
        whatsapp: {
          endpoint_url: draft.whatsapp.endpoint_url,
          templates: draft.whatsapp.templates,
        },
      };
      if (waTokenInput.trim()) {
        body.whatsapp.bearer_token = waTokenInput.trim();
      }
      const out = await api('/admin/notification-settings', {
        method: 'PUT',
        body: JSON.stringify(body),
      });
      setDraft(out);
      setWaTokenInput('');
      onSuccess('Configurações WhatsApp gravadas.');
    } catch (e) {
      onError(e.message || 'Erro ao gravar');
    } finally {
      setSavingWa(false);
    }
  }

  async function saveEmail() {
    if (!draft) return;
    setSavingEmail(true);
    onError('');
    try {
      const body = {
        email: {
          smtp: {
            ...draft.email.smtp,
          },
          templates: draft.email.templates,
        },
      };
      if (smtpPassInput.trim()) {
        body.email.smtp.smtp_password = smtpPassInput.trim();
      }
      delete body.email.smtp.smtp_password_configured;
      const out = await api('/admin/notification-settings', {
        method: 'PUT',
        body: JSON.stringify(body),
      });
      setDraft(out);
      setSmtpPassInput('');
      onSuccess('Configurações de e-mail gravadas.');
    } catch (e) {
      onError(e.message || 'Erro ao gravar');
    } finally {
      setSavingEmail(false);
    }
  }

  async function sendLogsCleanupAlertForced() {
    if (!window.confirm('Enviar e-mail de limpeza agora (ignora limiar e intervalo)?')) return;
    setLogsCleanupAlertBusy(true);
    onError('');
    try {
      const res = await api('/admin/logs-cleanup-alert', {
        method: 'POST',
        body: JSON.stringify({ force: true }),
      });
      if (res.sent) {
        onSuccess(`E-mail enviado (${res.recipients ?? 1} destinatário(s)). Verifique a caixa de entrada.`);
        await reload();
      } else {
        onError(res.reason ? `Não enviado: ${res.reason}` : 'SMTP ou destinatários em falta.');
      }
    } catch (e) {
      onError(e.message || 'Erro ao enviar.');
    } finally {
      setLogsCleanupAlertBusy(false);
    }
  }

  async function previewLogsCleanupTemplate() {
    if (!logsCleanupTemplate) return;
    onError('');
    try {
      const d = await api('/admin/logs-cleanup-link');
      const vars = {
        totalLogs: String(d.counts?.total ?? 0),
        totalErp: String(d.counts?.erp ?? 0),
        totalAuditNoise: String(d.counts?.audit_noise ?? 0),
        data: new Date().toLocaleString('pt-BR'),
        linkLimpar: String(d.url ?? ''),
      };
      setEmailPreview({
        title: logsCleanupTemplate.title || 'Limpeza de logs',
        subject: fillTemplatePlaceholders(logsCleanupTemplate.subject, vars),
        html: fillTemplatePlaceholders(logsCleanupTemplate.body_html, vars),
      });
    } catch (e) {
      onError(e.message || 'Não foi possível gerar o link de teste (confirme deploy de logs_cleanup.php).');
    }
  }

  async function openLogsCleanupTestLink() {
    onError('');
    try {
      const d = await api('/admin/logs-cleanup-link');
      if (d.url) {
        window.open(d.url, '_blank', 'noopener,noreferrer');
        onSuccess('Link de teste aberto num novo separador.');
      } else {
        onError('Link não gerado.');
      }
    } catch (e) {
      onError(e.message || 'Erro ao obter link de teste.');
    }
  }

  async function testWhatsapp(ev) {
    const eventKey =
      typeof ev === 'string' ? ev : ev?.currentTarget?.dataset?.event || waTemplates[0]?.event_key;
    if (!eventKey) return;
    onError('');
    try {
      const res = await api('/admin/templates/whatsapp-test', {
        method: 'POST',
        body: JSON.stringify({ event_key: eventKey, test_fast_secondary: true }),
      });
      const pr = res?.results?.primary;
      const sec = res?.results?.secondary;
      let msg = `Disparo 1: HTTP ${pr?.http_code ?? '—'} ${pr?.ok ? 'OK' : 'falhou'}`;
      if (sec) {
        msg += ` · Disparo 2: HTTP ${sec.http_code} ${sec.ok ? 'OK' : 'falhou'}`;
      }
      onSuccess(msg);
    } catch (e) {
      onError(e.message || 'Falha no teste WhatsApp');
    }
  }

  async function sendEmailTest(templateEventKey = '') {
    if (!testEmailAddr.trim()) {
      onError('Indique o e-mail para o teste.');
      return;
    }
    setTestEmailBusy(true);
    onError('');
    try {
      const payload = { to: testEmailAddr.trim() };
      if (templateEventKey) {
        payload.event_key = templateEventKey;
      } else {
        payload.subject = regTemplate?.subject || 'Teste OminIA';
        payload.html =
          regTemplate?.body_html ||
          '<p>Teste de envio. Se chegou, o SMTP está correcto.</p>';
      }
      await api('/admin/templates/email-test', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      onSuccess('E-mail de teste enviado.');
    } catch (e) {
      onError(e.message || 'Falha no envio');
    } finally {
      setTestEmailBusy(false);
    }
  }

  async function sendBroadcast() {
    setBroadcastBusy(true);
    onError('');
    try {
      const body = {
        subject: broadcastSubject.trim(),
        body_text: broadcastBody,
        mode: broadcastMode === 'all' ? 'all' : 'user',
      };
      if (broadcastMode === 'user') {
        body.user_id = Number(broadcastUserId);
      }
      const res = await api('/admin/email-broadcast', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      const fail = (res.failures || []).length;
      onSuccess(
        `Enviados: ${res.sent ?? 0}${fail ? ` · falhas: ${fail}` : ''}`
      );
    } catch (e) {
      onError(e.message || 'Falha no envio em massa');
    } finally {
      setBroadcastBusy(false);
    }
  }

  if (loading) {
    return (
      <div className="rounded-3xl border border-ink/10 bg-white/80 p-8 text-sm text-ink/60 dark:border-white/10 dark:bg-white/5">
        A carregar templates…
      </div>
    );
  }

  if (!draft) {
    return (
      <div className="rounded-3xl border border-accent2/40 bg-accent2/10 p-6 text-sm font-semibold text-accent2">
        Não foi possível carregar as definições. Confirme que importou{' '}
        <code className="font-mono">database/0001_schema.sql</code> no MySQL.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {emailPreview && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="email-preview-title"
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4 backdrop-blur-[1px]"
          onClick={() => setEmailPreview(null)}
        >
          <div
            className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-ink/15 bg-white shadow-xl dark:border-white/15 dark:bg-[#1a1f1c]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex shrink-0 items-start justify-between gap-3 border-b border-ink/10 px-5 py-4 dark:border-white/10">
              <div className="min-w-0">
                <h4 id="email-preview-title" className="font-semibold text-ink dark:text-mist">
                  {emailPreview.title}
                </h4>
                <p className="mt-1 truncate text-xs text-ink/55 dark:text-frost-muted" title={emailPreview.subject}>
                  Assunto (exemplo):{' '}
                  <span className="font-medium text-ink dark:text-mist">
                    {emailPreview.subject?.trim() ? emailPreview.subject : '(sem assunto)'}
                  </span>
                </p>
              </div>
              <button
                type="button"
                className="shrink-0 rounded-xl border border-ink/15 px-3 py-1.5 text-sm font-semibold dark:border-white/20"
                onClick={() => setEmailPreview(null)}
              >
                Fechar
              </button>
            </div>
            <p className="shrink-0 px-5 pt-2 text-[11px] text-ink/55 dark:text-mist/72">
              As variáveis conhecidas foram substituídas por valores fictícios só para visualizar o layout.
            </p>
            <div className="min-h-0 flex-1 p-4">
              <iframe
                title="Pré-visualização do e-mail"
                className="h-[min(70vh,560px)] w-full rounded-xl border border-ink/10 bg-white dark:border-white/10"
                sandbox=""
                srcDoc={wrapEmailPreviewDocument(emailPreview.html)}
              />
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2 rounded-3xl bg-black/5 p-2 dark:bg-white/10">
        {[
          ['whatsapp', 'WhatsApp'],
          ['email', 'E-mail'],
        ].map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => setSub(id)}
            className={`rounded-2xl px-4 py-2 text-xs font-semibold uppercase ${
              sub === id
                ? 'bg-white text-ink shadow dark:bg-ink dark:text-mist'
                : 'text-ink/60 dark:text-frost-muted'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="rounded-2xl border border-ink/10 bg-gradient-to-r from-white/90 via-white/70 to-cyan-500/[0.06] p-2.5 dark:border-white/10 dark:from-white/[0.06] dark:via-white/[0.04] dark:to-cyan-400/[0.08]">
        <div className="mb-1.5 flex items-center justify-between gap-2 px-0.5">
          <p className="text-[9px] font-bold uppercase tracking-[0.2em] text-ink/45 dark:text-frost-muted">
            Estado rápido · templates
          </p>
          <p className="text-[9px] text-ink/40 dark:text-frost-muted">
            {sub === 'whatsapp' ? 'Canal WhatsApp' : 'Canal e-mail'}
          </p>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {sub === 'whatsapp' ? (
            <>
              <TemplateKpiChip
                label="Token WA"
                value={templateKpis.waTok ? 'OK' : 'Falta'}
                tone={templateKpis.waTok ? 'ok' : 'warn'}
                detail="Token Bearer da API WhatsApp"
              />
              <TemplateKpiChip
                label="Endpoint"
                value={templateKpis.waEndpoint ? 'Definido' : 'Vazio'}
                tone={templateKpis.waEndpoint ? 'ok' : 'warn'}
              />
              <TemplateKpiChip
                label="Modelos WA"
                value={`${templateKpis.waActive}/${templateKpis.waTotal}`}
                tone={templateKpis.waActive > 0 ? 'ok' : 'off'}
                detail="Activos / total"
              />
              <TemplateKpiChip
                label="Inactivos"
                value={String(Math.max(0, templateKpis.waTotal - templateKpis.waActive))}
                tone={templateKpis.waTotal - templateKpis.waActive > 0 ? 'info' : 'off'}
              />
            </>
          ) : (
            <>
              <TemplateKpiChip
                label="SMTP"
                value={templateKpis.smtpOn ? 'Ativo' : 'Off'}
                tone={templateKpis.smtpOn ? 'ok' : 'off'}
              />
              <TemplateKpiChip
                label="Senha SMTP"
                value={templateKpis.smtpPass ? 'OK' : 'Falta'}
                tone={templateKpis.smtpPass ? 'ok' : 'warn'}
              />
              <TemplateKpiChip
                label="Modelos e-mail"
                value={`${templateKpis.emActive}/${templateKpis.emTotal}`}
                tone={templateKpis.emActive > 0 ? 'ok' : 'off'}
                detail="Activos / total"
              />
              <TemplateKpiChip
                label="Alertas"
                value={templateKpis.alertCount > 0 ? `${templateKpis.alertCount} dest.` : 'Nenhum'}
                tone={templateKpis.alertCount > 0 ? 'info' : 'warn'}
                detail="E-mails de alerta (cadastros / logs)"
              />
            </>
          )}
        </div>
      </div>

      {sub === 'whatsapp' && (
        <div className="space-y-6">
          <div className="rounded-3xl border border-ink/10 bg-white/80 p-6 dark:border-white/10 dark:bg-white/5">
            <h3 className="font-semibold text-ink dark:text-mist">Credenciais da API (ChatPagePro)</h3>
            <p className="mt-1 text-xs text-ink/55 dark:text-frost-muted">
              Endpoint e token Bearer usados em todos os modelos abaixo (POST JSON com{' '}
              <code className="font-mono">number</code> e <code className="font-mono">body</code>).
            </p>
            <div className="mt-4 grid gap-3 md:grid-cols-2">
              <label className="block text-xs font-semibold text-ink/80 dark:text-mist/75">
                Endpoint
                <input
                  className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 font-mono text-xs dark:border-white/10 dark:bg-[#252f2a]"
                  value={draft.whatsapp.endpoint_url}
                  onChange={(e) =>
                    setDraft((d) =>
                      d ? { ...d, whatsapp: { ...d.whatsapp, endpoint_url: e.target.value } } : d
                    )
                  }
                />
              </label>
              <label className="block text-xs font-semibold text-ink/80 dark:text-mist/75">
                Token Bearer
                <span className="ml-2 font-normal text-ink/45">
                  {draft.whatsapp.token_configured ? '(já guardado — deixe vazio para manter)' : ''}
                </span>
                <div className="relative mt-1">
                  <input
                    type={showWaToken ? 'text' : 'password'}
                    autoComplete="off"
                    className="w-full rounded-xl border border-ink/10 bg-white px-3 py-2 pr-10 font-mono text-xs dark:border-white/10 dark:bg-[#252f2a]"
                    placeholder="Novo token…"
                    value={waTokenInput}
                    onChange={(e) => setWaTokenInput(e.target.value)}
                  />
                  <button
                    type="button"
                    className="absolute top-1/2 right-2 -translate-y-1/2 text-xs text-ink/50"
                    onClick={() => setShowWaToken(!showWaToken)}
                  >
                    {showWaToken ? 'ocultar' : 'mostrar'}
                  </button>
                </div>
              </label>
            </div>
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                disabled={savingWa}
                onClick={saveWhatsapp}
                className="rounded-2xl bg-ink px-5 py-2 text-sm font-semibold text-mist disabled:opacity-50 dark:bg-mist dark:text-ink"
              >
                {savingWa ? 'A gravar…' : 'Gravar credenciais e modelos WA'}
              </button>
            </div>
          </div>

          {waTemplates.map((t) => (
            <div
              key={t.event_key}
              className="rounded-3xl border border-emerald-500/25 bg-gradient-to-br from-white/90 via-white/80 to-emerald-500/[0.07] p-5 dark:border-emerald-400/20 dark:from-white/[0.06] dark:via-white/[0.04] dark:to-emerald-500/[0.08]"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-semibold text-ink dark:text-mist">{t.title}</h3>
                    <TemplateActivePill enabled={Boolean(t.enabled)} />
                    <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-emerald-800 dark:text-emerald-100">
                      WhatsApp
                    </span>
                  </div>
                  <p className="font-mono text-[11px] text-ink/50 dark:text-mist/72">
                    Evento: {t.event_key}
                  </p>
                </div>
                <Toggle
                  label="Ativo"
                  on={t.enabled}
                  onChange={(v) => patchWaTemplate(t.event_key, { enabled: v })}
                />
              </div>

              <div className="mt-4 grid gap-4 lg:grid-cols-2">
                <div className="rounded-2xl border border-emerald-500/20 bg-white/70 p-3 dark:border-emerald-400/15 dark:bg-black/20">
                  <p className="mb-2 text-[9px] font-bold uppercase tracking-[0.16em] text-emerald-800/80 dark:text-emerald-200/80">
                    Edição
                  </p>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <label className="block text-xs font-semibold">
                      Número padrão
                      <input
                        className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 font-mono text-sm dark:border-white/10 dark:bg-[#252f2a]"
                        placeholder="ex. 5511999990000"
                        value={t.phone_primary}
                        onChange={(e) =>
                          patchWaTemplate(t.event_key, { phone_primary: e.target.value })
                        }
                      />
                    </label>
                    <label className="block text-xs font-semibold">
                      Número 2 (disparo após pausa)
                      <input
                        className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 font-mono text-sm dark:border-white/10 dark:bg-[#252f2a]"
                        value={t.phone_secondary}
                        onChange={(e) =>
                          patchWaTemplate(t.event_key, { phone_secondary: e.target.value })
                        }
                      />
                    </label>
                    <label className="block text-xs font-semibold sm:col-span-2">
                      Atraso entre número 1 e 2 (segundos)
                      <input
                        type="number"
                        min={0}
                        max={120}
                        className="mt-1 w-40 rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-[#252f2a]"
                        value={t.secondary_delay_secs}
                        onChange={(e) =>
                          patchWaTemplate(t.event_key, {
                            secondary_delay_secs: Number(e.target.value),
                          })
                        }
                      />
                    </label>
                  </div>
                  <label className="mt-3 block text-xs font-semibold">
                    Mensagem (template)
                    <textarea
                      rows={8}
                      className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 font-mono text-sm dark:border-white/10 dark:bg-[#252f2a]"
                      value={t.message_body}
                      onChange={(e) =>
                        patchWaTemplate(t.event_key, { message_body: e.target.value })
                      }
                    />
                  </label>
                  <p className={TEMPLATE_LEGEND_CLS}>
                    WhatsApp: *negrito*, _itálico_, ~tachado~. Variáveis do evento:{' '}
                    {t.event_key === 'admin_new_platform_user'
                      ? '{nome}, {email}, {plano}, {userId}, {data}'
                      : t.event_key === 'admin_new_tenant_store'
                        ? '{nomeLoja}, {slug}, {donoUserId}, {emailDono}, {tenantId}, {data}'
                        : t.event_key === 'order_confirmation_customer' ||
                            t.event_key === 'order_confirmation_store_owner'
                          ? '{numeroPedido}, {pedidoId}, {tenantId}, {nomeCliente}, {telefoneCliente}, {emailCliente}, {nomeLoja}, {slug}, {itensResumo}, {subtotal}, {descontoPercent}, {cupomCodigo}, {valorDesconto}, {blocoCupom}, {taxaEntrega}, {zonaEntrega}, {blocoFrete} (vazio sem entrega), {total}, {formaPagamento} (informação de checkout; «—» se não houver), {observacoes}, {data}, {telefoneLoja} (dígitos loja), {telefoneLojaFormatado} (BR)'
                          : '{data}'}
                    .
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button
                      type="button"
                      data-event={t.event_key}
                      onClick={() => testWhatsapp(t.event_key)}
                      className="rounded-2xl border border-emerald-600/30 bg-emerald-500/10 px-4 py-2 text-sm font-semibold text-emerald-950 dark:border-emerald-400/30 dark:bg-emerald-500/15 dark:text-emerald-50"
                    >
                      Testar disparo
                    </button>
                  </div>
                </div>
                <WhatsAppLivePreview text={t.message_body} />
              </div>
            </div>
          ))}
        </div>
      )}

      {sub === 'email' && (
        <div className="space-y-6">
          <div className="rounded-3xl border border-ink/10 bg-white/80 p-6 dark:border-white/10 dark:bg-white/5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="font-semibold">Configuração SMTP</h3>
                <TemplateActivePill enabled={Boolean(smtp.enabled)} />
              </div>
              <Toggle
                label="SMTP ativo"
                on={smtp.enabled}
                onChange={(v) => patchSmtp({ enabled: v })}
              />
            </div>
            <div className="mt-4 grid gap-3 md:grid-cols-2">
              <label className="block text-xs font-semibold">
                Host
                <input
                  className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-[#252f2a]"
                  value={smtp.smtp_host}
                  onChange={(e) => patchSmtp({ smtp_host: e.target.value })}
                />
              </label>
              <label className="block text-xs font-semibold">
                Porta
                <input
                  type="number"
                  className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-[#252f2a]"
                  value={smtp.smtp_port}
                  onChange={(e) => patchSmtp({ smtp_port: Number(e.target.value) })}
                />
              </label>
              <label className="block text-xs font-semibold">
                Usuário
                <input
                  className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-[#252f2a]"
                  value={smtp.smtp_username}
                  onChange={(e) => patchSmtp({ smtp_username: e.target.value })}
                />
              </label>
              <label className="block text-xs font-semibold">
                Senha
                <div className="relative mt-1">
                  <input
                    type={showSmtpPass ? 'text' : 'password'}
                    autoComplete="new-password"
                    placeholder={
                      smtp.smtp_password_configured
                        ? '(guardada — preencha para alterar)'
                        : 'Senha SMTP'
                    }
                    className="w-full rounded-xl border border-ink/10 bg-white px-3 py-2 pr-10 text-sm dark:border-white/10 dark:bg-[#252f2a]"
                    value={smtpPassInput}
                    onChange={(e) => setSmtpPassInput(e.target.value)}
                  />
                  <button
                    type="button"
                    className="absolute top-1/2 right-2 -translate-y-1/2 text-xs text-ink/50"
                    onClick={() => setShowSmtpPass(!showSmtpPass)}
                  >
                    {showSmtpPass ? 'ocultar' : 'mostrar'}
                  </button>
                </div>
              </label>
              <label className="block text-xs font-semibold">
                De e-mail
                <input
                  type="email"
                  className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-[#252f2a]"
                  value={smtp.from_email}
                  onChange={(e) => patchSmtp({ from_email: e.target.value })}
                />
              </label>
              <label className="block text-xs font-semibold">
                De nome
                <input
                  className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-[#252f2a]"
                  value={smtp.from_name}
                  onChange={(e) => patchSmtp({ from_name: e.target.value })}
                />
              </label>
              <label className="md:col-span-2 block text-xs font-semibold">
                E-mails de alerta (cadastros)
                <span className="block font-normal text-ink/50 dark:text-mist/72">
                  Separe por vírgula. Recebem cadastros novos e, se activo abaixo, o alerta de limpeza de logs.
                </span>
                <input
                  className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-[#252f2a]"
                  placeholder="voce@empresa.com, outro@empresa.com"
                  value={smtp.alert_to_emails ?? ''}
                  onChange={(e) => patchSmtp({ alert_to_emails: e.target.value })}
                />
              </label>
            </div>

            <div className="mt-6 rounded-2xl border border-amber-500/25 bg-amber-500/[0.06] p-4 dark:border-amber-400/20 dark:bg-amber-500/10">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h4 className="text-sm font-semibold text-ink dark:text-mist">Limpeza de logs por e-mail</h4>
                <Toggle
                  label="Alerta automático"
                  on={Boolean(smtp.logs_cleanup_enabled)}
                  onChange={(v) => patchSmtp({ logs_cleanup_enabled: v })}
                />
              </div>
              <p className="mt-2 text-[11px] leading-relaxed text-ink/55 dark:text-frost-muted">
                Quando o volume de logs ERP + notificações ultrapassar o limiar, envia um e-mail com botão
                «Limpar logs». Um clique apaga o log de integração ERP e auditoria ruidosa (não apaga pedidos).
              </p>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <label className="block text-xs font-semibold">
                  Limiar (registos)
                  <input
                    type="number"
                    min={100}
                    max={5000000}
                    className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-[#252f2a]"
                    value={smtp.logs_cleanup_min_rows ?? 2500}
                    onChange={(e) => patchSmtp({ logs_cleanup_min_rows: Number(e.target.value) })}
                  />
                </label>
                <label className="block text-xs font-semibold">
                  Intervalo mínimo (horas)
                  <input
                    type="number"
                    min={1}
                    max={8760}
                    className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-[#252f2a]"
                    value={smtp.logs_cleanup_alert_hours ?? 168}
                    onChange={(e) => patchSmtp({ logs_cleanup_alert_hours: Number(e.target.value) })}
                  />
                </label>
              </div>
              {smtp.logs_cleanup_last_alert_at ? (
                <p className="mt-2 text-[11px] text-ink/50 dark:text-frost-muted">
                  Último alerta: {String(smtp.logs_cleanup_last_alert_at)}
                </p>
              ) : null}
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={logsCleanupAlertBusy || savingEmail}
                  onClick={sendLogsCleanupAlertForced}
                  className="rounded-2xl border border-amber-600/40 px-4 py-2 text-xs font-semibold text-amber-900 dark:text-amber-100"
                >
                  {logsCleanupAlertBusy ? 'A enviar…' : 'Enviar e-mail de teste agora'}
                </button>
              </div>
            </div>
            <p className="mt-3 text-[11px] text-ink/50 dark:text-frost-muted">
              O teste abaixo usa o mesmo SMTP. Se o teste chegar, os alertas de cadastro também enviam (com SMTP
              ativo e e-mails de alerta preenchidos). Pode indicar vários destinatários separados por vírgula.
            </p>
            <div className="mt-4 flex flex-wrap items-end gap-2">
              <input
                type="text"
                inputMode="email"
                autoComplete="email"
                placeholder="ex.: um@dominio.pt ou dois@a.pt, tres@b.pt"
                className="min-w-[200px] flex-1 rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-[#252f2a]"
                value={testEmailAddr}
                onChange={(e) => setTestEmailAddr(e.target.value)}
              />
              <button
                type="button"
                disabled={testEmailBusy}
                onClick={() => sendEmailTest('admin_new_registration')}
                className="rounded-2xl bg-ink px-4 py-2 text-sm font-semibold text-mist disabled:opacity-50 dark:bg-mist dark:text-ink"
              >
                {testEmailBusy ? 'A enviar…' : 'Enviar teste'}
              </button>
            </div>
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                disabled={savingEmail}
                onClick={saveEmail}
                className="rounded-2xl border border-ink/20 px-5 py-2 text-sm font-semibold dark:border-white/20 disabled:opacity-50"
              >
                {savingEmail ? 'A gravar…' : 'Gravar SMTP'}
              </button>
            </div>
          </div>

          {broadTemplate && (
            <div className="rounded-3xl border border-ink/10 bg-white/80 p-6 dark:border-white/10 dark:bg-white/5">
              <h3 className="font-semibold">Comunicados gerais — enviar agora</h3>
              <p className="mt-1 text-xs text-ink/55 dark:text-frost-muted">
                Texto livre (emojis incluídos). O destinatário é um cliente da plataforma ({' '}
                <strong>utilizador</strong> com login) ou todos com conta activa e e-mail válido.
              </p>
              <label className="mt-4 block text-xs font-semibold">
                Assunto
                <input
                  className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-[#252f2a]"
                  value={broadcastSubject}
                  onChange={(e) => setBroadcastSubject(e.target.value)}
                />
              </label>
              <label className="mt-3 block text-xs font-semibold">
                Mensagem
                <textarea
                  rows={8}
                  className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-[#252f2a]"
                  placeholder="Cole aqui o texto com emojis…"
                  value={broadcastBody}
                  onChange={(e) => setBroadcastBody(e.target.value)}
                />
              </label>
              <div className="mt-3 flex flex-wrap gap-3">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="bc-mode"
                    checked={broadcastMode === 'user'}
                    onChange={() => setBroadcastMode('user')}
                  />
                  Um cliente
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="bc-mode"
                    checked={broadcastMode === 'all'}
                    onChange={() => setBroadcastMode('all')}
                  />
                  Todos os utilizadores
                </label>
              </div>
              {broadcastMode === 'user' && (
                <select
                  className="mt-2 w-full max-w-md rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-[#252f2a]"
                  value={broadcastUserId}
                  onChange={(e) => setBroadcastUserId(e.target.value)}
                >
                  <option value="">— Escolha o utilizador —</option>
                  {users
                    .filter((u) => !u.blocked)
                    .map((u) => (
                      <option key={u.id} value={u.id}>
                        #{u.id} · {u.name} · {u.email}
                      </option>
                    ))}
                </select>
              )}
              <button
                type="button"
                disabled={broadcastBusy || !broadcastSubject.trim() || !broadcastBody.trim()}
                onClick={sendBroadcast}
                className="mt-4 rounded-2xl bg-accent px-5 py-2 text-sm font-semibold text-white disabled:opacity-45"
              >
                {broadcastBusy ? 'A enviar…' : 'Enviar comunicado'}
              </button>

              <div className="mt-8 border-t border-ink/10 pt-6 dark:border-white/10">
                <div className="flex flex-wrap items-center gap-2">
                  <h4 className="text-sm font-semibold">Modelo guardado ({broadTemplate.event_key})</h4>
                  <TemplateActivePill enabled={Boolean(broadTemplate.enabled)} />
                </div>
                <p className="text-[11px] text-ink/50">
                  Rascunho HTML opcional nas definições (não é usado automaticamente no envio rápido acima).
                </p>
                <Toggle
                  label="Modelo referência activo"
                  on={broadTemplate.enabled}
                  onChange={(v) =>
                    patchEmailTemplate(broadTemplate.event_key, { enabled: v })
                  }
                />
                <label className="mt-3 block text-xs font-semibold">
                  Assunto (modelo)
                  <input
                    className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-[#252f2a]"
                    value={broadTemplate.subject}
                    onChange={(e) =>
                      patchEmailTemplate(broadTemplate.event_key, { subject: e.target.value })
                  }
                  />
                </label>
                <textarea
                  rows={6}
                  className="mt-2 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 font-mono text-xs dark:border-white/10 dark:bg-[#252f2a]"
                  value={broadTemplate.body_html}
                  onChange={(e) =>
                    patchEmailTemplate(broadTemplate.event_key, { body_html: e.target.value })
                  }
                />
                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      setEmailPreview({
                        title: 'Comunicados — modelo HTML guardado',
                        subject: fillEmailPreviewPlaceholders(broadTemplate.subject),
                        html: fillEmailPreviewPlaceholders(broadTemplate.body_html),
                      })
                    }
                    className="rounded-2xl border border-ink/20 px-4 py-2 text-sm font-semibold dark:border-white/25"
                  >
                    Pré-visualizar modelo
                  </button>
                  <button
                    type="button"
                    disabled={savingEmail}
                    onClick={saveEmail}
                    className="rounded-2xl border border-ink/20 px-4 py-2 text-sm font-semibold disabled:opacity-50"
                  >
                    Gravar modelo comunicados
                  </button>
                </div>
              </div>
            </div>
          )}

          {extraEmailTemplates.length > 0 && (
            <div className="rounded-3xl border border-ink/10 bg-white/80 p-6 dark:border-white/10 dark:bg-white/5">
              <h3 className="font-semibold text-ink dark:text-mist">
                Modelos automáticos (pedidos e outros)
              </h3>
              <p className="mt-1 text-xs text-ink/55 dark:text-frost-muted">
                Estes eventos enviam e-mail ao cliente quando o estado do pedido muda (se o modelo estiver activo e o
                SMTP configurado). Use o botão «Testar este modelo» em cada cartão.
              </p>
            </div>
          )}

          {extraEmailTemplates.map((t) => (
            <div
              key={t.event_key}
              className="rounded-3xl border border-violet-500/25 bg-gradient-to-br from-white/90 via-white/80 to-violet-500/[0.08] p-5 dark:border-violet-400/20 dark:from-white/[0.06] dark:via-white/[0.04] dark:to-violet-500/[0.09]"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-semibold text-ink dark:text-mist">{t.title}</h3>
                    <TemplateActivePill enabled={Boolean(t.enabled)} />
                    <span className="rounded-full bg-violet-500/15 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-violet-900 dark:text-violet-100">
                      E-mail
                    </span>
                  </div>
                  <p className="font-mono text-[11px] text-ink/50 dark:text-mist/72">Evento: {t.event_key}</p>
                </div>
                <Toggle
                  label="Ativo"
                  on={t.enabled}
                  onChange={(v) => patchEmailTemplate(t.event_key, { enabled: v })}
                />
              </div>
              <div className="mt-4 grid gap-4 lg:grid-cols-2">
                <div className="rounded-2xl border border-violet-500/20 bg-white/70 p-3 dark:border-violet-400/15 dark:bg-black/20">
                  <p className="mb-2 text-[9px] font-bold uppercase tracking-[0.16em] text-violet-800/80 dark:text-violet-200/80">
                    Edição
                  </p>
                  <label className="block text-xs font-semibold">
                    Assunto
                    <input
                      className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-[#252f2a]"
                      value={t.subject}
                      onChange={(e) => patchEmailTemplate(t.event_key, { subject: e.target.value })}
                    />
                  </label>
                  <label className="mt-3 block text-xs font-semibold">
                    Corpo HTML
                    <textarea
                      rows={10}
                      className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 font-mono text-xs dark:border-white/10 dark:bg-[#252f2a]"
                      value={t.body_html}
                      onChange={(e) => patchEmailTemplate(t.event_key, { body_html: e.target.value })}
                    />
                  </label>
                  <p className={TEMPLATE_LEGEND_CLS}>
                    Variáveis:{' '}
                    {t.event_key === 'order_status_paid_customer' ||
                    t.event_key === 'order_status_erp_customer' ||
                    t.event_key === 'order_status_cancelled_customer'
                      ? '{numeroPedido}, {pedidoId}, {tenantId}, {nomeCliente}, {telefoneCliente}, {emailCliente}, {nomeLoja}, {slug}, {itensResumo}, {taxaEntrega}, {zonaEntrega}, {blocoFrete}, {total}, {formaPagamento}, {observacoes}, {data}, {telefoneLoja}, {telefoneLojaFormatado}, {linkLoja}'
                      : '{data} (e variáveis específicas do evento, se existirem)'}
                    .
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() =>
                        setEmailPreview({
                          title: t.title || t.event_key,
                          subject: fillEmailPreviewPlaceholders(t.subject),
                          html: fillEmailPreviewPlaceholders(t.body_html),
                        })
                      }
                      className="rounded-2xl border border-violet-600/30 bg-violet-500/10 px-4 py-2 text-sm font-semibold text-violet-950 dark:border-violet-400/30 dark:bg-violet-500/15 dark:text-violet-50"
                    >
                      Pré-visualizar ampliado
                    </button>
                    <button
                      type="button"
                      disabled={testEmailBusy}
                      onClick={() => sendEmailTest(t.event_key)}
                      className="rounded-2xl border border-ink/20 px-4 py-2 text-sm font-semibold dark:border-white/25 disabled:opacity-50"
                    >
                      {testEmailBusy ? 'A enviar…' : 'Testar este modelo'}
                    </button>
                    <button
                      type="button"
                      disabled={savingEmail}
                      onClick={saveEmail}
                      className="rounded-2xl bg-ink px-4 py-2 text-sm font-semibold text-mist disabled:opacity-50 dark:bg-mist dark:text-ink"
                    >
                      Gravar
                    </button>
                  </div>
                </div>
                <EmailLivePreview subject={t.subject} html={t.body_html} />
              </div>
            </div>
          ))}

          {regTemplate && (
            <div className="rounded-3xl border border-sky-500/25 bg-gradient-to-br from-white/90 via-white/80 to-sky-500/[0.08] p-5 dark:border-sky-400/20 dark:from-white/[0.06] dark:via-white/[0.04] dark:to-sky-500/[0.09]">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-semibold">{regTemplate.title}</h3>
                    <TemplateActivePill enabled={Boolean(regTemplate.enabled)} />
                    <span className="rounded-full bg-sky-500/15 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-sky-900 dark:text-sky-100">
                      E-mail
                    </span>
                  </div>
                  <p className="font-mono text-[11px] text-ink/50 dark:text-white/70">{regTemplate.event_key}</p>
                </div>
                <Toggle
                  label="Ativo"
                  on={regTemplate.enabled}
                  onChange={(v) =>
                    patchEmailTemplate(regTemplate.event_key, { enabled: v })
                  }
                />
              </div>
              <div className="mt-4 grid gap-4 lg:grid-cols-2">
                <div className="rounded-2xl border border-sky-500/20 bg-white/70 p-3 dark:border-sky-400/15 dark:bg-black/20">
                  <p className="mb-2 text-[9px] font-bold uppercase tracking-[0.16em] text-sky-800/80 dark:text-sky-200/80">
                    Edição
                  </p>
                  <label className="block text-xs font-semibold">
                    Assunto
                    <input
                      className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-[#252f2a]"
                      value={regTemplate.subject}
                      onChange={(e) =>
                        patchEmailTemplate(regTemplate.event_key, { subject: e.target.value })
                      }
                    />
                  </label>
                  <label className="mt-3 block text-xs font-semibold">
                    Corpo HTML
                    <textarea
                      rows={10}
                      className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 font-mono text-xs dark:border-white/10 dark:bg-[#252f2a]"
                      value={regTemplate.body_html}
                      onChange={(e) =>
                        patchEmailTemplate(regTemplate.event_key, { body_html: e.target.value })
                      }
                    />
                  </label>
                  <p className={TEMPLATE_LEGEND_CLS}>
                    Variáveis:{' '}
                    {'{tipoCadastro}, {nome}, {email}, {plano}, {nomeLoja}, {slug}, {data}'}.
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() =>
                        setEmailPreview({
                          title: regTemplate.title || 'Cadastros',
                          subject: fillEmailPreviewPlaceholders(regTemplate.subject),
                          html: fillEmailPreviewPlaceholders(regTemplate.body_html),
                        })
                      }
                      className="rounded-2xl border border-sky-600/30 bg-sky-500/10 px-4 py-2 text-sm font-semibold text-sky-950 dark:border-sky-400/30 dark:bg-sky-500/15 dark:text-sky-50"
                    >
                      Pré-visualizar ampliado
                    </button>
                    <button
                      type="button"
                      disabled={savingEmail}
                      onClick={saveEmail}
                      className="rounded-2xl border border-ink/20 px-4 py-2 text-sm font-semibold disabled:opacity-50"
                    >
                      Gravar modelo de cadastro
                    </button>
                  </div>
                </div>
                <EmailLivePreview subject={regTemplate.subject} html={regTemplate.body_html} />
              </div>
            </div>
          )}

          {logsCleanupTemplate && (
            <div className="rounded-3xl border border-amber-500/30 bg-gradient-to-br from-white/90 via-white/80 to-amber-500/[0.09] p-5 dark:border-amber-400/25 dark:from-white/[0.06] dark:via-white/[0.04] dark:to-amber-500/[0.1]">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-semibold">{logsCleanupTemplate.title}</h3>
                    <TemplateActivePill enabled={Boolean(logsCleanupTemplate.enabled)} />
                    <span className="rounded-full bg-amber-500/20 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-amber-950 dark:text-amber-100">
                      E-mail
                    </span>
                  </div>
                  <p className="font-mono text-[11px] text-ink/50 dark:text-white/70">{logsCleanupTemplate.event_key}</p>
                </div>
                <Toggle
                  label="Ativo"
                  on={logsCleanupTemplate.enabled}
                  onChange={(v) =>
                    patchEmailTemplate(logsCleanupTemplate.event_key, { enabled: v })
                  }
                />
              </div>
              <div className="mt-4 grid gap-4 lg:grid-cols-2">
                <div className="rounded-2xl border border-amber-500/25 bg-white/70 p-3 dark:border-amber-400/20 dark:bg-black/20">
                  <p className="mb-2 text-[9px] font-bold uppercase tracking-[0.16em] text-amber-900/80 dark:text-amber-200/80">
                    Edição
                  </p>
                  <label className="block text-xs font-semibold">
                    Assunto
                    <input
                      className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-[#252f2a]"
                      value={logsCleanupTemplate.subject}
                      onChange={(e) =>
                        patchEmailTemplate(logsCleanupTemplate.event_key, { subject: e.target.value })
                      }
                    />
                  </label>
                  <label className="mt-3 block text-xs font-semibold">
                    Corpo HTML
                    <textarea
                      rows={12}
                      className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 font-mono text-xs dark:border-white/10 dark:bg-[#252f2a]"
                      value={logsCleanupTemplate.body_html}
                      onChange={(e) =>
                        patchEmailTemplate(logsCleanupTemplate.event_key, { body_html: e.target.value })
                      }
                    />
                  </label>
                  <p className={TEMPLATE_LEGEND_CLS}>
                    Variáveis:{' '}
                    {'{totalLogs}, {totalErp}, {totalAuditNoise}, {data}, {linkLimpar}'}.
                    O teste e a pré-visualização ampliada geram um <strong className="text-ink dark:text-white">link real</strong> (válido 7 dias).
                  </p>
                  <div className="mt-3 flex flex-wrap items-end gap-2">
                    <input
                      type="text"
                      inputMode="email"
                      autoComplete="email"
                      placeholder="seu@email.com"
                      className="min-w-[200px] flex-1 rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-[#252f2a]"
                      value={testEmailAddr}
                      onChange={(e) => setTestEmailAddr(e.target.value)}
                    />
                    <button
                      type="button"
                      disabled={testEmailBusy}
                      onClick={() => sendEmailTest('admin_logs_cleanup_prompt')}
                      className="rounded-2xl bg-ink px-4 py-2 text-sm font-semibold text-mist disabled:opacity-50 dark:bg-mist dark:text-ink"
                    >
                      {testEmailBusy ? 'A enviar…' : 'Testar este modelo'}
                    </button>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => previewLogsCleanupTemplate()}
                      className="rounded-2xl border border-amber-600/35 bg-amber-500/10 px-4 py-2 text-sm font-semibold text-amber-950 dark:border-amber-400/30 dark:bg-amber-500/15 dark:text-amber-50"
                    >
                      Pré-visualizar ampliado
                    </button>
                    <button
                      type="button"
                      onClick={() => openLogsCleanupTestLink()}
                      className="rounded-2xl border border-emerald-600/40 px-4 py-2 text-sm font-semibold text-emerald-800 dark:text-emerald-200"
                    >
                      Abrir link de teste
                    </button>
                    <button
                      type="button"
                      disabled={savingEmail}
                      onClick={saveEmail}
                      className="rounded-2xl border border-ink/20 px-4 py-2 text-sm font-semibold disabled:opacity-50"
                    >
                      Gravar modelo de limpeza
                    </button>
                  </div>
                </div>
                <EmailLivePreview
                  subject={logsCleanupTemplate.subject}
                  html={logsCleanupTemplate.body_html}
                />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
